import { useMutation } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import type { DomainCheckResult, Money } from "@websede/contracts";
import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../api/client.js";
import { useSession } from "../auth/session.js";
import { PublicLayout } from "../components/public-layout.js";
import { useFeatures } from "../features.js";

function formatMoney(money: Money, locale: string): string {
  return new Intl.NumberFormat(locale, { style: "currency", currency: money.currency }).format(
    money.amountMinor / 100,
  );
}

/**
 * Public storefront: search domains at cost without an account. "Register" leads to sign-up with
 * the domain remembered, or straight to checkout for a signed-in administrator.
 */
export function StorePage() {
  const { t, i18n } = useTranslation();
  const session = useSession();
  const features = useFeatures();
  const [query, setQuery] = useState("");
  const check = useMutation({ mutationFn: (domains: string[]) => api.domains.check({ domains }) });

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    const domains = query
      .split(/[\s,]+/)
      .map((d) => d.trim().toLowerCase())
      .filter(Boolean)
      .map((d) => (d.includes(".") ? d : `${d}.com`));
    if (domains.length > 0) check.mutate(domains.slice(0, 20));
  };

  return (
    <PublicLayout>
      <main className="mx-auto flex max-w-4xl flex-col gap-8 px-4 py-10">
        <section className="flex flex-col gap-3">
          <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl">
            {t("store.headline")}
          </h1>
          <p className="max-w-2xl text-base text-ink-2">{t("store.subline")}</p>
          <div className="mt-2 flex flex-wrap gap-3">
            <Link
              to={session.data ? "/mail" : "/signup"}
              className={`rounded-full bg-accent px-5 py-2.5 text-sm font-medium text-white hover:shadow-1 ${session.data || features.loaded ? "" : "invisible"}`}
            >
              {session.data
                ? t("store.openWorkspace")
                : features.signup
                  ? t("public.nav.startTrial")
                  : t("public.nav.comingSoon")}
            </Link>
            <Link
              to="/pricing"
              className="rounded-full border border-ink-3/40 px-5 py-2.5 text-sm font-medium text-accent hover:bg-accent-soft/60"
            >
              {t("public.home.seePricing")}
            </Link>
          </div>
        </section>
        {features.domainSales ? (
          <>
            <form onSubmit={onSubmit} className="flex flex-col gap-2 sm:flex-row">
              <input
                className="flex-1 rounded-md border border-rule bg-surface px-4 py-3 text-base outline-none focus:border-accent"
                placeholder={t("domains.searchPlaceholder")}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                autoComplete="off"
                spellCheck={false}
              />
              <button
                type="submit"
                className="rounded-md bg-accent px-6 py-3 text-base font-medium text-white disabled:opacity-60"
                disabled={check.isPending}
              >
                {check.isPending ? t("common.loading") : t("domains.search")}
              </button>
            </form>
            <p className="text-xs text-ink-3">{t("domains.priceNote")}</p>
            {check.isError ? (
              <p className="text-sm text-danger">{t("common.errorGeneric")}</p>
            ) : null}
            {check.data ? (
              <ul className="divide-y divide-rule rounded-md border border-rule bg-surface">
                {check.data.results.map((result) => (
                  <StoreRow
                    key={result.domain}
                    result={result}
                    locale={i18n.language}
                    signedIn={Boolean(session.data)}
                  />
                ))}
              </ul>
            ) : null}
          </>
        ) : null}
        <section className="grid gap-4 sm:grid-cols-3">
          {(["suite", "private", "cost"] as const).map((key) => (
            <div key={key} className="rounded-md border border-rule bg-surface p-4">
              <h2 className="text-sm font-semibold">{t(`store.points.${key}.title`)}</h2>
              <p className="mt-1 text-sm text-ink-2">{t(`store.points.${key}.body`)}</p>
            </div>
          ))}
        </section>
      </main>
    </PublicLayout>
  );
}

function StoreRow({
  result,
  locale,
  signedIn,
}: {
  result: DomainCheckResult;
  locale: string;
  signedIn: boolean;
}) {
  const { t } = useTranslation();
  const status = t(
    `domains.${result.availability === "unknown" ? "unsupported" : result.availability}`,
  );
  const tone =
    result.availability === "available"
      ? "text-good"
      : result.availability === "premium"
        ? "text-accent"
        : "text-ink-3";
  const canRegister = result.registration && result.availability === "available";
  const buttonClass =
    "rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-white hover:opacity-90";
  return (
    <li className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
      <div className="min-w-0">
        <div className="truncate font-mono text-sm">{result.domain}</div>
        <div className={`text-xs ${tone}`}>{status}</div>
      </div>
      {canRegister && result.registration ? (
        <div className="flex items-center gap-4">
          <div className="text-right">
            <div className="text-sm font-medium tabular-nums">
              {formatMoney(result.registration.total, locale)}
            </div>
            <div className="text-xs text-ink-3">{t("domains.perYear")}</div>
          </div>
          {signedIn ? (
            <Link
              to="/admin/domains/checkout"
              search={{ domain: result.domain, years: 1 }}
              className={buttonClass}
            >
              {t("domains.register")}
            </Link>
          ) : (
            <Link to="/signup" search={{ domain: result.domain }} className={buttonClass}>
              {t("domains.register")}
            </Link>
          )}
        </div>
      ) : null}
    </li>
  );
}
